import { useEffect, useRef } from 'react';

import { CURRENT_POINT_KEY, IS_NEED_SHOW_LICENSES_MODAL, PERSISTED_LICENSES_KEY } from './licensesModalStorageKeys';
import { LicensesModalAction, LicensesModalState } from './licensesModalReducer';

interface BillingData {
  hasInactiveLicenses: boolean;
  hasActiveLicenses: boolean;
  unpaidLicensesIds: string[];
  unpaidLicenses: { id: string }[];
  fullPeriodAmount?: number | null;
}

interface Props {
  billingData: BillingData | null;
  unpaidLicensesOnly: boolean;
  dispatch: (value: LicensesModalAction) => void;
  setSubscriptionCost: (licensesIds: string[]) => Promise<void>;
  setExtendSubscriptionCost: (licensesIds: string[]) => Promise<void>;
}

const useLicensesModalRestoreState = ({
  billingData,
  unpaidLicensesOnly,
  dispatch,
  setSubscriptionCost,
  setExtendSubscriptionCost,
}: Props) => {
  const isInitialized = useRef<boolean>(false);
  const isSetUnpaidAsToEnable = useRef<boolean>(false);

  const setToEnableAutoRenewalFromUnpaid = async () => {
    if (!billingData) return;

    dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: billingData.unpaidLicensesIds });
    await setSubscriptionCost(billingData.unpaidLicensesIds);
  };

  const restoreState = async () => {
    const persistedStateString = localStorage.getItem(PERSISTED_LICENSES_KEY);

    if (!billingData || !persistedStateString || persistedStateString === 'undefined') {
      return false;
    }

    const persistedState = JSON.parse(persistedStateString) as LicensesModalState;
    const unpaidIds = persistedState.toEnableAutorenewal.filter((id) =>
      billingData.unpaidLicenses.find((license) => license.id === id)
    );

    dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: persistedState.toEnableAutorenewal });
    dispatch({ type: 'setToSuspendAutorenewal', toSuspendAutorenewal: persistedState.toSuspendAutorenewal });
    await setExtendSubscriptionCost(unpaidIds);

    localStorage.removeItem(PERSISTED_LICENSES_KEY);
    localStorage.removeItem(IS_NEED_SHOW_LICENSES_MODAL);

    return true;
  };

  useEffect(() => {
    if (!billingData) return;

    if (isSetUnpaidAsToEnable.current) return;

    if (unpaidLicensesOnly) {
      setToEnableAutoRenewalFromUnpaid();
      isSetUnpaidAsToEnable.current = true;
    }
  }, [billingData, isSetUnpaidAsToEnable.current]);

  useEffect(() => {
    if (billingData?.fullPeriodAmount) {
      dispatch({
        type: 'setLicensesSubscriptionFullPeriodCost',
        licensesSubscriptionFullPeriodCost: billingData.fullPeriodAmount,
      });
    }
  }, [billingData?.fullPeriodAmount]);

  useEffect(() => {
    if (!billingData) return;

    if (isInitialized.current) return;

    const { hasInactiveLicenses, hasActiveLicenses, unpaidLicensesIds } = billingData;

    const initializeState = async () => {
      const restored = await restoreState();

      const currentPointString = localStorage.getItem(CURRENT_POINT_KEY);
      const hasCurrentPoint = currentPointString && currentPointString !== 'undefined';

      if (!restored && (hasInactiveLicenses || hasActiveLicenses) && hasCurrentPoint) {
        dispatch({ type: 'setToEnableAutorenewal', toEnableAutorenewal: unpaidLicensesIds });

        if (hasActiveLicenses) {
          await setExtendSubscriptionCost(unpaidLicensesIds);
        }
      }
    };

    initializeState();

    isInitialized.current = true;
  }, [billingData, isInitialized.current]);
};

export default useLicensesModalRestoreState;
